/**
 * CORE-897 — SignalR watch-party hub lifecycle telemetry (client-side only).
 *
 * The hub connection (`useWatchPartyHub`) carries everything in a live session
 * except audio/video: roster, raised hands, chat, pins, session phase, ending.
 * Its state was measured by NOTHING — the admin's only connection telemetry is
 * `room_rtc.*`, which is Agora/LiveKit, a *different* socket. So «периодически
 * отваливается» (CORE-759/750/751/850) could neither be confirmed nor denied
 * without reading both sides of the code by hand.
 *
 * This is the pure heart of the fix: a reducer that turns the hook's existing
 * 4-value `connectionState` stream into a small, self-collapsing set of
 * lifecycle events. Kept free of React and SignalR on purpose — the whole point
 * is that the emission rules are decidable and testable in isolation (the
 * CORE-756 lesson: a telemetry event whose fields can't answer the question is
 * a wasted retest).
 *
 * ─── Why drive it off `connectionState`, not the raw SignalR callbacks ───
 * The hook already funnels every path — SDK auto-reconnect (`onreconnecting`/
 * `onreconnected`), terminal close (`onclose`), the CORE-750 watchdog rebuild,
 * and a failed JoinParty — into one `connectionState`. A state value only
 * changes on a real transition, so reducing over transitions is inherently
 * deduped: it cannot double-fire the way subscribing to every raw callback
 * would.
 *
 * ─── What collapses (the volume guard the ticket asks for) ───
 * The hub drops in *series*: SDK auto-reconnect walks its 8.5-min ladder, then
 * the watchdog rebuilds every 5–60s for as long as the room stays open. A naive
 * "track every attempt" would flood analytics during a single outage. Here an
 * outage is ONE `disconnected` event when it starts and ONE `reconnected` event
 * when (if) it recovers — the many intermediate `connecting`/`disconnected`
 * cycles in between only bump counters, they emit nothing. «Доля
 * невосстановившихся» is then just the disconnects with no matching reconnect
 * before the session ends.
 */

export type HubConnState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export type HubLifecyclePhase = 'connected' | 'disconnected' | 'reconnected';

/**
 * The payload half of a `room_hub_lifecycle` analytics event. The hook adds the
 * shared context (partyId / role / platform) around it before tracking.
 *
 * Field rationale — the answer to «как часто и надолго рвётся у обычного
 * участника»:
 * - `reason` — WHY it dropped, as far as the client can tell it apart:
 *   `reconnecting` (SDK is auto-retrying, likely recoverable) vs `closed`
 *   (socket closed outright — deploy, terminal give-up, or a JoinParty that
 *   never landed). One `reason` was not enough in CORE-756; two decidable
 *   values are the minimum that separates "trying" from "gave up".
 * - `uptimeMs` — how long the socket lived before the drop, so a client that
 *   flaps every few seconds is visible next to one that ran clean for an hour.
 * - `downtimeMs` — how long the outage lasted end to end (median of this over a
 *   day is AC #2).
 * - `sequence` — WHICH drop this is in the session (1-based). Distinguishes
 *   "recovered on the first blip" from "this client has dropped 15 times".
 * - `attempts` — how many (re)connect attempts the outage took to recover, so
 *   the "номер попытки" survives even though the SDK hides its internal ladder.
 * - `recoverable` — was the drop entered via SDK auto-reconnect (true) or a
 *   hard close (false); pairs with `reason`.
 */
export interface HubLifecycleEvent {
  phase: HubLifecyclePhase;
  reason?: 'reconnecting' | 'closed';
  uptimeMs?: number;
  downtimeMs?: number;
  sequence?: number;
  attempts?: number;
  recoverable?: boolean;
}

export interface HubLifecycleTrackerState {
  /** True once the hub has connected at least once — so the very first
   *  `disconnected → connecting → connected` on mount reads as an initial
   *  connect, not a reconnection. */
  everConnected: boolean;
  /** When the current live socket connected (ms epoch), or null while down. */
  connectedAtMs: number | null;
  /** When the current outage began (ms epoch), or null while connected. Set
   *  once at the head of an outage and held across every intermediate
   *  connecting/disconnected cycle until recovery. */
  downSinceMs: number | null;
  /** Count of outages so far this session (the `sequence` field). */
  sequence: number;
  /** (Re)connect attempts observed within the current outage. */
  attempts: number;
}

export const initialHubLifecycleState = (): HubLifecycleTrackerState => ({
  everConnected: false,
  connectedAtMs: null,
  downSinceMs: null,
  sequence: 0,
  attempts: 0,
});

export interface HubLifecycleStep {
  state: HubLifecycleTrackerState;
  event?: HubLifecycleEvent;
}

/**
 * Advance the tracker by one `connectionState` transition and, when the
 * transition is worth recording, return the event to emit. Pure: same inputs →
 * same output, no clock/IO of its own (the caller passes `nowMs`).
 *
 * The caller must skip no-op transitions (prev === next) itself; this only
 * receives genuine changes.
 */
export function advanceHubLifecycle(
  state: HubLifecycleTrackerState,
  prev: HubConnState,
  next: HubConnState,
  nowMs: number,
): HubLifecycleStep {
  // Any (re)connect attempt made while an outage is open — count it so the
  // recovery event can report the attempt number without emitting per attempt.
  if (next === 'connecting' && state.downSinceMs != null) {
    return { state: { ...state, attempts: state.attempts + 1 } };
  }

  if (next === 'connected') {
    if (state.downSinceMs != null) {
      // Recovery — the outage that started at `downSinceMs` is over.
      const event: HubLifecycleEvent = {
        phase: 'reconnected',
        downtimeMs: Math.max(0, nowMs - state.downSinceMs),
        sequence: state.sequence,
        attempts: Math.max(1, state.attempts),
      };
      return {
        state: { ...state, everConnected: true, connectedAtMs: nowMs, downSinceMs: null, attempts: 0 },
        event,
      };
    }
    if (!state.everConnected) {
      // First time the hub comes up this session.
      return {
        state: { ...state, everConnected: true, connectedAtMs: nowMs },
        event: { phase: 'connected' },
      };
    }
    // Already connected with no outage open (e.g. a redundant re-assert) — keep
    // an uptime anchor but say nothing.
    return { state: { ...state, connectedAtMs: state.connectedAtMs ?? nowMs } };
  }

  // Open a new outage — only from a live connection, and only once per outage.
  // A `reconnecting → disconnected` handoff (SDK exhausted its ladder) keeps the
  // same outage open and emits nothing: that is the series-collapse.
  const opensOutage =
    (next === 'reconnecting' || next === 'disconnected') &&
    prev === 'connected' &&
    state.downSinceMs == null &&
    state.everConnected;

  if (opensOutage) {
    const recoverable = next === 'reconnecting';
    const sequence = state.sequence + 1;
    const event: HubLifecycleEvent = {
      phase: 'disconnected',
      reason: recoverable ? 'reconnecting' : 'closed',
      uptimeMs: state.connectedAtMs != null ? Math.max(0, nowMs - state.connectedAtMs) : undefined,
      sequence,
      recoverable,
    };
    return {
      state: { ...state, connectedAtMs: null, downSinceMs: nowMs, sequence, attempts: 0 },
      event,
    };
  }

  return { state };
}
